// Salida deliberada del kiosco (P1).
//
// Problema: la pantalla es fullscreen sin navegación — el usuario `pantalla` no
// tenía NINGUNA forma de cerrar sesión (mover el TV, cambiarle la planta o
// entrar con otro usuario exigía borrar los datos del navegador).
//
// Por qué un gesto y no un botón: un "Cerrar sesión" visible en una TV de piso
// es un peligro operativo — cualquiera lo toca y la pantalla queda muerta hasta
// que alguien con la contraseña la reactive. El gesto (N toques seguidos sobre
// el logo) es intencional, no se descubre por accidente y funciona con mouse,
// touch y con el OK del control remoto (el logo es un <button> enfocable).

import { useCallback, useEffect, useRef, useState } from 'react';

/** Toques necesarios y ventana para encadenarlos. */
export const TOQUES_PARA_SALIR = 5;
const VENTANA_MS = 3000;
/** El diálogo se cierra solo: nadie deja el kiosco "abierto" por curiosear. */
const AUTOCIERRE_MS = 15_000;

interface SalidaKiosco {
  /** Toques encadenados hasta ahora (para dar feedback visual). */
  progreso: number;
  /** ¿Mostrar el diálogo de confirmación? */
  abierto: boolean;
  /** Registrar un toque en el logo. */
  toque: () => void;
  /** Cerrar el diálogo sin salir. */
  cancelar: () => void;
}

export function useSalidaKiosco(): SalidaKiosco {
  const [progreso, setProgreso] = useState(0);
  const [abierto, setAbierto] = useState(false);
  const ultimo = useRef(0);

  const toque = useCallback(() => {
    const ahora = Date.now();
    // Fuera de la ventana, la cuenta arranca de nuevo en 1.
    const n = ahora - ultimo.current > VENTANA_MS ? 1 : progreso + 1;
    ultimo.current = ahora;
    if (n >= TOQUES_PARA_SALIR) {
      setProgreso(0);
      setAbierto(true);
    } else {
      setProgreso(n);
    }
  }, [progreso]);

  const cancelar = useCallback(() => {
    setAbierto(false);
    setProgreso(0);
  }, []);

  // El progreso a medias se olvida al vencer la ventana.
  useEffect(() => {
    if (progreso === 0) return;
    const t = window.setTimeout(() => setProgreso(0), VENTANA_MS);
    return () => window.clearTimeout(t);
  }, [progreso]);

  // Autocierre del diálogo.
  useEffect(() => {
    if (!abierto) return;
    const t = window.setTimeout(() => setAbierto(false), AUTOCIERRE_MS);
    return () => window.clearTimeout(t);
  }, [abierto]);

  return { progreso, abierto, toque, cancelar };
}
